import React from 'react';
import { translate } from 'react-i18next';

// To the two decimals the API rounds to, so a figure reads as the mobile apps print it.
const formatFigure = value => (value == null ? '–' : Math.round(value * 100) / 100);

// "12/20", or the bare "12" where the progress payload set no target.
const ratio = (actual, target, format = String) => (
  target > 0 ? `${format(actual)}/${format(target)}` : `${format(actual)}`
);

// Props: block (as SelfScheduleStore labels it), progress (the block's progress row), fteCounters
// (its Day, Night and Weekend obligations, empty where the FTE model does not run), eligibleCount,
// month (a moment), canGoBack, canGoForward and onMonthChange. Reuses #calendar-header from
// _calendar.scss; the block's range sits where TODAY does, as a label, and its location and
// department sit under the month name, so the page names the block the way its menu row does.
function SelfScheduleToolbar(props) {
  const {
    block, progress, fteCounters, eligibleCount, month, canGoBack, canGoForward, onMonthChange, t,
  } = props;

  return (
    <div id="calendar-header">
      <div id="calendar-header-wrapper">
        <div id="calendar-meta-container">
          <div id="month-toggle">
            <button
              type="button"
              id="previous-month"
              className="previous-arrow"
              onClick={() => onMonthChange(moment(month).subtract(1, 'month'))}
              disabled={!canGoBack}
              aria-label={t('SelfScheduleView.previous_month')}
            />
            <button type="button" id="today-button" disabled>{block.label.toUpperCase()}</button>
            <button
              type="button"
              id="next-month"
              className="next-arrow"
              onClick={() => onMonthChange(moment(month).add(1, 'month'))}
              disabled={!canGoForward}
              aria-label={t('SelfScheduleView.next_month')}
            />
          </div>
          <div id="month-label">
            {moment(month).format('MMMM YYYY')}
            {block.locationLabel && (
              <span className="self-schedule__block-location">{block.locationLabel}</span>
            )}
          </div>
          <div id="schedule-legend">
            <div className="legend-item">
              <span className="legend-item-label">{t('SelfScheduleView.eligible_shifts')}</span>
              <span className="legend-item-count scheduled">{eligibleCount}</span>
            </div>
          </div>
        </div>
        {/* The employee's own tally, beside the legend and above the sidebar. Every denominator
            is the progress payload's own.
            ponytail: target_shift_count is not in the payload yet (counters.shifts carries
            total/direct/indirect only and targets are manager-only), so the shifts pill shows
            the bare count until the MS-5456 backend ask lands. */}
        <div className="self-schedule__progress">
          <span className="self-schedule__pill self-schedule__pill--shifts">
            {t('SelfScheduleView.shifts', { value: ratio(progress.shift_count, progress.target_shift_count) })}
          </span>
          <span className="self-schedule__pill self-schedule__pill--hours">
            {t('SelfScheduleView.hours', {
              value: ratio(progress.hours_requested, progress.target_hours, formatFigure),
            })}
          </span>
          {/* Always actual/target, as the mobile apps print them, with a dash for no target. The
              title names the rule the letter stands for, in the admin's words. */}
          {fteCounters.length > 0 && (
            <span className="self-schedule__fte">
              {fteCounters.map(counter => (
                <span
                  key={counter.core_kind}
                  className={`self-schedule__pill is-${counter.status || 'unset'}`}
                  title={counter.name}
                >
                  {t(`SelfScheduleView.${counter.core_kind}`, {
                    value: `${formatFigure(counter.actual)}/${formatFigure(counter.target)}`,
                  })}
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default translate()(SelfScheduleToolbar);
